// src/admin-components/AdminDashboard.jsx
import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import StatCard from "./StatCard";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { getDashboardStats, getAdmins, createAdmin, deleteAdmin } from "../api/adminApi";
import "./admin-dashboard.css";

// const BASE_URL = "https://api.suvidhaeldercare.com/api";
const BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/api$/, "") || "https://api.suvidhaeldercare.com";

function authHeaders() {
  const t = localStorage.getItem("token");
  return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const COLORS = ["#1F4E8C","#F39C12","#4CAF50","#E74C3C","#9B59B6","#1ABC9C"];

// ── Mini Stat Card ────────────────────────────────────────
function MiniStat({ icon, label, value, sub, color = "#1F4E8C", bg = "#EFF6FF" }) {
  return (
    <div className="adnd-mini-stat" style={{ borderTop: `4px solid ${color}` }}>
      <div className="adnd-mini-icon" style={{ background: bg }}>{icon}</div>
      <div className="adnd-mini-body">
        <div className="adnd-mini-val">{value}</div>
        <div className="adnd-mini-label">{label}</div>
        {sub && <div className="adnd-mini-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── Add Admin Modal ───────────────────────────────────────
function AddAdminModal({ onClose, onSuccess }) {
  const [form, setForm]       = useState({ full_name: "", phone: "", email: "", password: "", role: "admin" });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handleChange = (e) => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.password) return setError("Name, phone and password are required.");
    if (!/^[0-9]{10}$/.test(form.phone)) return setError("Enter a valid 10-digit phone.");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");
    try {
      setLoading(true);
      await createAdmin(form);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create admin");
    } finally { setLoading(false); }
  };

  return (
    <div className="adnd-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="adnd-modal">
        <div className="adnd-modal-head">
          <h3>➕ Add New Admin</h3>
          <button onClick={onClose} className="adnd-modal-close">✕</button>
        </div>
        {error && <div className="adnd-error">⚠ {error}</div>}
        <form onSubmit={handleSubmit} className="adnd-form">
          <div className="adnd-field"><label>Full Name *</label><input name="full_name" value={form.full_name} onChange={handleChange} placeholder="e.g. Rahul Sharma" /></div>
          <div className="adnd-field"><label>Phone * (10 digits)</label><input name="phone" value={form.phone} onChange={handleChange} placeholder="9999999999" maxLength={10} inputMode="numeric" /></div>
          <div className="adnd-field"><label>Email (optional)</label><input name="email" value={form.email} type="email" onChange={handleChange} placeholder="admin@suvidha.com" /></div>
          <div className="adnd-field"><label>Password * (min 6 chars)</label><input name="password" value={form.password} type="password" onChange={handleChange} placeholder="Enter password" /></div>
          <div className="adnd-field"><label>Role</label>
            <select name="role" value={form.role} onChange={handleChange}>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <button type="submit" className="adnd-submit-btn" disabled={loading}>{loading ? "Creating..." : "Create Account"}</button>
        </form>
      </div>
    </div>
  );
}

// ── MAIN DASHBOARD ────────────────────────────────────────
export default function AdminDashboard() {
  const [stats,       setStats]      = useState(null);
  const [admins,      setAdmins]     = useState([]);
  const [revData,     setRevData]    = useState(null);
  const [bookingStats, setBookingStats] = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [showModal,   setShowModal]  = useState(false);

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const isSuperAdmin = currentUser?.role === "super_admin";

  const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

  const fetchAll = async () => {
    try {
      setLoading(true);

      // Fetch dashboard stats
      const statsData = await getDashboardStats();
      setStats(statsData);

      // Fetch revenue (for charts)
      const revRes = await fetch(`${BASE_URL}/api/admin/revenue`, { headers: authHeaders() });
      if (revRes.ok) { const d = await revRes.json(); setRevData(d); }

      // Fetch booking analytics
      const bkRes = await fetch(`${BASE_URL}/api/bookings/admin/analytics`, { headers: authHeaders() });
      if (bkRes.ok) { const d = await bkRes.json(); setBookingStats(d); }

      if (isSuperAdmin) {
        const adminsData = await getAdmins();
        setAdmins(adminsData);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Revenue chart data from monthly breakdown
  const revenueChartData = revData?.monthly?.slice().reverse() || [];

  // Sub status pie
  const subStatusData = revData ? [
    { name: "Active",    value: revData.activeCount    || 0 },
    { name: "Expired",   value: revData.expiredCount   || 0 },
    { name: "Cancelled", value: revData.cancelledCount || 0 },
  ].filter(d => d.value > 0) : [];

  // Booking status data
  const bookingStatusData = bookingStats?.statusBreakdown?.map(s => ({
    name:  s.status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
    value: s.count,
  })) || [];

  return (
    <>
      {showModal && (
        <AddAdminModal onClose={() => setShowModal(false)} onSuccess={() => { fetchAll(); }} />
      )}

      <div className="adm-layout">
        <Sidebar />
        <main className="adm-main">
          <div className="adm-shell">
            <Topbar title="Dashboard" subtitle={`Welcome back, ${currentUser?.full_name?.split(" ")[0] || "Admin"}`} />

            {loading ? (
              <div className="adnd-loading">
                <div className="adnd-loading-spinner" />
                <span>Loading dashboard...</span>
              </div>
            ) : (
              <>
                {/* ── KPI Stats ───────────────────────────── */}
                <div className="adnd-kpi-grid">
                  <MiniStat icon="👤" label="Total Users"            value={(stats?.totalUsers || 0).toLocaleString()}          color="#1F4E8C" bg="#EFF6FF" />
                  <MiniStat icon="✅" label="Active Subscribers"     value={(stats?.activeSubscribers || 0).toLocaleString()}   color="#059669" bg="#D1FAE5" sub="No admin approval needed" />
                  <MiniStat icon="⏳" label="Expiring in 7 Days"     value={(stats?.expiringNext7Days || 0).toLocaleString()}   color="#DC2626" bg="#FEE2E2" sub="Needs follow-up" />
                  <MiniStat icon="🆕" label="New Signups This Month" value={(stats?.newSignupsThisMonth || 0).toLocaleString()} color="#0891B2" bg="#CFFAFE" />
                </div>

                {/* ── Revenue Cards ────────────────────────── */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:24 }}>

                  {/* Subscription Revenue */}
                  <div style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:18, padding:"22px 24px", borderTop:"4px solid #1F4E8C" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:".5px", marginBottom:8 }}>
                      💳 Subscription Revenue
                    </div>
                    <div style={{ fontSize:28, fontWeight:900, color:"#1F4E8C", marginBottom:4 }}>
                      {fmt(stats?.totalRevenueAllTime || 0)}
                    </div>
                    <div style={{ fontSize:13, color:"#94A3B8" }}>All time</div>
                    <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:12, color:"#64748B" }}>This month</span>
                      <span style={{ fontSize:15, fontWeight:800, color:"#059669" }}>{fmt(stats?.revenueThisMonth || 0)}</span>
                    </div>
                  </div>

                  {/* Booking Revenue */}
                  <div style={{ background:"#fff", border:"1px solid #E2E8F0", borderRadius:18, padding:"22px 24px", borderTop:"4px solid #F39C12" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:".5px", marginBottom:8 }}>
                      🛠 Booking Revenue
                    </div>
                    <div style={{ fontSize:28, fontWeight:900, color:"#D97706", marginBottom:4 }}>
                      {fmt(stats?.bookingRevenueAllTime || 0)}
                    </div>
                    <div style={{ fontSize:13, color:"#94A3B8" }}>All time · {(stats?.totalBookings || 0)} bookings</div>
                    <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid #F1F5F9", display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:12, color:"#64748B" }}>This month</span>
                      <span style={{ fontSize:15, fontWeight:800, color:"#059669" }}>{fmt(stats?.bookingRevenueThisMonth || 0)}</span>
                    </div>
                  </div>

                  {/* Combined Total */}
                  <div style={{ background:"linear-gradient(135deg,#1F4E8C,#2563EB)", borderRadius:18, padding:"22px 24px" }}>
                    <div style={{ fontSize:12, fontWeight:700, color:"rgba(255,255,255,.7)", textTransform:"uppercase", letterSpacing:".5px", marginBottom:8 }}>
                      💰 Total Revenue
                    </div>
                    <div style={{ fontSize:28, fontWeight:900, color:"#fff", marginBottom:4 }}>
                      {fmt((stats?.combinedRevenueAllTime) || (stats?.totalRevenueAllTime || 0) + (stats?.bookingRevenueAllTime || 0))}
                    </div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,.6)" }}>Subscriptions + Bookings</div>
                    <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid rgba(255,255,255,.2)", display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:12, color:"rgba(255,255,255,.7)" }}>This month</span>
                      <span style={{ fontSize:15, fontWeight:800, color:"#FDE68A" }}>
                        {fmt((stats?.combinedRevenueThisMonth) || (stats?.revenueThisMonth || 0) + (stats?.bookingRevenueThisMonth || 0))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── Revenue Area Chart ─────────────────── */}
                {revenueChartData.length > 0 && (
                  <div className="adnd-panel adnd-chart-panel">
                    <div className="adnd-panel-head">
                      <h3>📊 Monthly Revenue (Last 6 Months)</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={240}>
                      <AreaChart data={revenueChartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#1F4E8C" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#1F4E8C" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#64748B" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [fmt(v), "Revenue"]} labelStyle={{ fontWeight: 700 }} />
                        <Area type="monotone" dataKey="revenue" stroke="#1F4E8C" strokeWidth={2.5} fill="url(#revGrad)" dot={{ r: 4, fill: "#1F4E8C" }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* ── Row: Pie charts ─────────────────────── */}
                <div className="adnd-row-2">

                  {/* Subscription status pie */}
                  {subStatusData.length > 0 && (
                    <div className="adnd-panel adnd-chart-panel">
                      <div className="adnd-panel-head"><h3>💳 Subscription Status</h3></div>
                      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                        <ResponsiveContainer width="55%" height={180}>
                          <PieChart>
                            <Pie data={subStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                              {subStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="adnd-legend">
                          {subStatusData.map((d, i) => (
                            <div key={i} className="adnd-legend-item">
                              <span className="adnd-legend-dot" style={{ background: COLORS[i] }} />
                              <span>{d.name}</span>
                              <strong>{d.value}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Booking status pie */}
                  {bookingStatusData.length > 0 && (
                    <div className="adnd-panel adnd-chart-panel">
                      <div className="adnd-panel-head"><h3>📋 Booking Status</h3></div>
                      <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
                        <ResponsiveContainer width="55%" height={180}>
                          <PieChart>
                            <Pie data={bookingStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3} dataKey="value">
                              {bookingStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="adnd-legend">
                          {bookingStatusData.map((d, i) => (
                            <div key={i} className="adnd-legend-item">
                              <span className="adnd-legend-dot" style={{ background: COLORS[i] }} />
                              <span>{d.name}</span>
                              <strong>{d.value}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Revenue by Plan bar chart ─────────── */}
                {revData?.byPlan?.length > 0 && (
                  <div className="adnd-panel adnd-chart-panel">
                    <div className="adnd-panel-head"><h3>📦 Revenue by Subscription Plan</h3></div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={revData.byPlan} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="plan" tick={{ fontSize: 12, fill: "#64748B" }} />
                        <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => [fmt(v), "Revenue"]} />
                        <Bar dataKey="revenue" fill="#F39C12" radius={[6, 6, 0, 0]}>
                          {revData.byPlan.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* ── Subscription Info Note ─────────────── */}
                <div className="adnd-info-note">
                  <span className="adnd-info-icon">⚡</span>
                  <div>
                    <strong>Instant Subscription Activation</strong>
                    <p>Subscriptions purchased through the Plans page activate immediately after successful payment — no admin confirmation required. The system auto-verifies Razorpay payments and activates the plan.</p>
                  </div>
                </div>

                {/* ── Recent Expiring Subscriptions ─────── */}
                {stats?.expiringNext7Days > 0 && (
                  <div className="adnd-panel">
                    <div className="adnd-panel-head">
                      <h3>⏳ Expiring Soon (Next 7 Days)</h3>
                      <span className="adnd-badge-warn">{stats.expiringNext7Days} subscriptions</span>
                    </div>
                    <p style={{ fontSize: 13, color: "var(--muted-neutral)", margin: 0 }}>
                      These subscriptions expire within 7 days. Visit the{" "}
                      <a href="/admin/subscriptions" style={{ color: "var(--base-primary)", fontWeight: 700 }}>Subscriptions page</a>
                      {" "}to view details and follow up with users.
                    </p>
                  </div>
                )}

                {/* ── Admin Management (super_admin only) ── */}
                {isSuperAdmin && (
                  <div className="adnd-panel">
                    <div className="adnd-panel-head" style={{ flexWrap:"wrap", gap:10 }}>
                      <h3>👑 Admin Accounts</h3>
                      <button className="adnd-add-btn" onClick={() => setShowModal(true)}>
                        ➕ Add New Admin
                      </button>
                    </div>

                    {admins.length === 0 ? (
                      <div style={{ padding:10, color:"#64748B" }}>No admins found.</div>
                    ) : (
                      <>
                        {/* Desktop table */}
                        <div style={{ overflowX:"auto", display:"block" }} className="adnd-admin-table-wrap">
                          <table className="adm-table" style={{ minWidth:480 }}>
                            <thead>
                              <tr><th>ID</th><th>Name</th><th>Phone</th><th>Email</th><th>Role</th><th>Action</th></tr>
                            </thead>
                            <tbody>
                              {admins.map(a => (
                                <tr key={a.id}>
                                  <td>{a.id}</td>
                                  <td><strong>{a.full_name}</strong></td>
                                  <td>{a.phone}</td>
                                  <td>{a.email || "—"}</td>
                                  <td>
                                    <span className="adm-badge" style={a.role==="super_admin"?{background:"#FEF3C7",color:"#D97706"}:{}}>
                                      {a.role==="super_admin" ? "⭐ Super Admin" : "Admin"}
                                    </span>
                                  </td>
                                  <td>
                                    {a.role !== "super_admin" && (
                                      <button
                                        onClick={() => {
                                          if (!window.confirm(`Delete admin "${a.full_name}"?`)) return;
                                          deleteAdmin(a.id).then(fetchAll).catch(err => alert(err?.response?.data?.message || "Delete failed"));
                                        }}
                                        style={{ padding:"4px 10px", borderRadius:6, cursor:"pointer", background:"#FEE2E2", color:"#DC2626", border:"none", fontWeight:700, fontSize:12 }}
                                      >Delete</button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile cards */}
                        <div className="adnd-admin-cards">
                          {admins.map(a => (
                            <div key={a.id} style={{
                              background:"#F8FAFC", borderRadius:14,
                              border:"1px solid #E2E8F0", padding:"14px 16px",
                              display:"flex", justifyContent:"space-between",
                              alignItems:"center", gap:12
                            }}>
                              <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                                <div style={{
                                  width:40, height:40, borderRadius:"50%",
                                  background: a.role==="super_admin" ? "#FEF3C7" : "#EFF6FF",
                                  display:"flex", alignItems:"center", justifyContent:"center",
                                  fontSize:18, flexShrink:0
                                }}>
                                  {a.role==="super_admin" ? "⭐" : "👤"}
                                </div>
                                <div>
                                  <div style={{ fontWeight:800, fontSize:14, color:"#1E293B" }}>{a.full_name}</div>
                                  <div style={{ fontSize:12, color:"#64748B" }}>📱 {a.phone}</div>
                                  {a.email && <div style={{ fontSize:12, color:"#94A3B8" }}>✉️ {a.email}</div>}
                                  <span style={{
                                    fontSize:11, fontWeight:700, padding:"2px 8px",
                                    borderRadius:999, marginTop:4, display:"inline-block",
                                    background: a.role==="super_admin"?"#FEF3C7":"#EFF6FF",
                                    color:      a.role==="super_admin"?"#D97706":"#1D4ED8"
                                  }}>
                                    {a.role==="super_admin" ? "⭐ Super Admin" : "Admin"}
                                  </span>
                                </div>
                              </div>
                              {a.role !== "super_admin" && (
                                <button
                                  onClick={() => {
                                    if (!window.confirm(`Delete admin "${a.full_name}"?`)) return;
                                    deleteAdmin(a.id).then(fetchAll).catch(err => alert(err?.response?.data?.message || "Delete failed"));
                                  }}
                                  style={{ padding:"6px 12px", borderRadius:8, cursor:"pointer", background:"#FEE2E2", color:"#DC2626", border:"none", fontWeight:700, fontSize:12, flexShrink:0 }}
                                >Delete</button>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
}